// src/components/Events/AboutAICIS.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './Events.css';

const AboutAICIS = () => {
  const [activeTab, setActiveTab] = useState('overview');

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'theme', label: 'Theme & Objectives' },
    { id: 'governance', label: 'Governance' },
    { id: 'committee', label: 'Committee' }
  ];

  return (
    <div className="about-aicis-page">
      {/* ===== HERO SECTION ===== */}
      <div 
        className="about-aicis-hero"
        style={{
          backgroundImage: 'url(/images/presidency-villa.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundAttachment: 'fixed'
        }}
      >
        <div className="about-aicis-hero-overlay-dark"></div>
        <div className="about-aicis-hero-content">
          <span className="about-aicis-badge">ABOUT THE SUMMIT</span>
          <h1>AICIS 2026</h1>
          <h2>Africa Infrastructure, Climate Change, Security & Green Investment Summit</h2>
          <div className="about-aicis-stats">
            <div className="stat-item">
              <span className="stat-number">3,000+</span>
              <span className="stat-label">Delegates</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">56</span>
              <span className="stat-label">Countries</span>
            </div>
            <div className="stat-divider"></div>
            <div className="stat-item">
              <span className="stat-number">$12T</span>
              <span className="stat-label">Investment Potential</span>
            </div>
          </div>
        </div>
      </div>

      {/* ===== TABS ===== */}
      <div className="about-aicis-tabs">
        <div className="about-aicis-tabs-container">
          {tabs.map(tab => (
            <button
              key={tab.id}
              className={`about-aicis-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ===== CONTENT ===== */}
      <div className="about-aicis-content">
        <div className="about-aicis-content-inner">

          {/* ===== OVERVIEW TAB ===== */}
          {activeTab === 'overview' && (
            <div className="tab-content">

              {/* INTRODUCTION */}
              <div className="about-section introduction-section">
                <div className="introduction-box">
                  <span className="introduction-badge">INTRODUCTION</span>
                  <p className="introduction-text">
                    The Africa Infrastructure, Climate Change, Security & Green Investment Summit (AICIS 2026)
                    is Africa's premier high-level investment and policy platform dedicated to accelerating
                    sustainable infrastructure development, climate resilience, regional security, and green
                    economic transformation across the continent.
                  </p>
                  <p className="introduction-text">
                    Building on the remarkable success of AICIS 2025, which convened over 3,000 delegates from
                    governments, development finance institutions, diplomatic missions, multilateral organizations,
                    private sector leaders, academia, and civil society, AICIS 2026 returns on a significantly
                    larger global scale to drive investment, innovation, and strategic partnerships for Africa's
                    sustainable future.
                  </p>
                  <p className="introduction-text">
                    Convened by the <strong>Africa Business Ventures & Investment Group (ABVIG)</strong> in
                    collaboration with the <strong>Office of the Secretary to the Government of the Federation (OSGF)</strong>,
                    <strong>Presidency of the Federal Republic of Nigeria</strong>, the
                    <strong>Federal Ministry of Foreign Affairs (MFA)</strong>, and relevant
                    <strong>Federal Ministries, Departments and Agencies (MDAs)</strong>, the Summit serves as a
                    strategic platform for mobilizing global capital and strengthening public-private partnerships
                    to deliver bankable infrastructure and climate-smart development projects across Africa.
                  </p>
                  <p className="introduction-text">
                    Scheduled to hold from <strong>25–28 November 2026</strong> in <strong>Abuja, Nigeria</strong>,
                    AICIS 2026 will convene over 3,000 high-level participants, including Heads of State and Government,
                    Ministers, Ambassadors, Development Finance Institutions, Multilateral Organizations, Investors,
                    Financial Institutions, Corporate Executives, Project Developers, Researchers, and Development
                    Partners from Africa and around the world.
                  </p>
                  <p className="introduction-text">
                    The Summit provides an action-oriented platform where governments, investors, financial institutions,
                    development partners, and the private sector collaborate to transform Africa's infrastructure and
                    climate ambitions into investment-ready and bankable projects. Through strategic dialogue, project
                    matchmaking, investment showcases, and policy engagement, AICIS 2026 will accelerate financing for
                    resilient infrastructure, clean energy, industrialization, food security, digital transformation,
                    healthcare, transport, mining, and sustainable urban development.
                  </p>
                  <p className="introduction-text">
                    Aligned with the <strong>African Union Agenda 2063</strong>, the <strong>United Nations Sustainable
                      Development Goals (SDGs)</strong>, the <strong>Paris Agreement on Climate Change</strong>, the
                    <strong>African Continental Free Trade Area (AfCFTA)</strong>, and <strong>Nigeria's Renewed Hope Agenda</strong>,
                    AICIS 2026 seeks to strengthen regional cooperation, unlock climate finance, expand investment
                    opportunities, and position Africa as a globally competitive destination for sustainable
                    infrastructure and green investment.
                  </p>
                </div>
              </div>

              {/* Strategic Alignment */}
              <div className="about-section">
                <h3>Strategic Alignment</h3>
                <p>AICIS 2026 supports the implementation of key global, continental, and national development frameworks:</p>
                <div className="alignment-grid">
                  <div className="alignment-item">
                    <span className="alignment-icon">🇺🇳</span>
                    <span>UN Sustainable Development Goals (SDGs)</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🌍</span>
                    <span>Paris Agreement on Climate Change</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🌍</span>
                    <span>African Union Agenda 2063</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🌍</span>
                    <span>AU Climate Change & Resilient Development Strategy</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">📊</span>
                    <span>African Continental Free Trade Area (AfCFTA)</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🇳🇬</span>
                    <span>Nigeria's National Climate Change Action Plan</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🇳🇬</span>
                    <span>Nigeria's Renewed Hope Agenda</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🇳🇬</span>
                    <span>Nigeria's Decade of Gas Initiative</span>
                  </div>
                  <div className="alignment-item">
                    <span className="alignment-icon">🇳🇬</span>
                    <span>Petroleum Industry Act (PIA)</span>
                  </div>
                </div>
              </div>

              {/* Summit Focus Areas */}
              <div className="about-section">
                <h3>Summit Focus Areas</h3>
                <div className="focus-grid-about">
                  <div className="focus-item-about">⚡ Renewable Energy & Energy Transition</div>
                  <div className="focus-item-about">💰 Climate Finance & Carbon Markets</div>
                  <div className="focus-item-about">🏗️ Infrastructure & PPPs</div>
                  <div className="focus-item-about">🚆 Transport, Ports & Logistics</div>
                  <div className="focus-item-about">💻 Digital Economy & AI</div>
                  <div className="focus-item-about">🏙️ Smart Cities & Affordable Housing</div>
                  <div className="focus-item-about">⛏️ Sustainable Mining & Critical Minerals</div>
                  <div className="focus-item-about">🌾 Agriculture & Agro-Industrial Development</div>
                  <div className="focus-item-about">🌊 Blue Economy & Water Resources</div>
                  <div className="focus-item-about">🛢️ Oil, Gas & Energy Security</div>
                  <div className="focus-item-about">🏥 Healthcare Infrastructure</div>
                  <div className="focus-item-about">🏭 Manufacturing & Industrialization</div>
                  <div className="focus-item-about">🌿 Climate Adaptation & Sustainability</div>
                  <div className="focus-item-about">🤝 Trade, Investment & Regional Integration</div>
                </div>
              </div>

              {/* Summit Experience */}
              <div className="about-section with-image">
                <div className="about-section-content">
                  <h3>Summit Experience</h3>
                  <p>AICIS 2026 will be delivered as a world-class hybrid event, combining physical participation with global virtual engagement.</p>
                  <div className="experience-grid">
                    <div className="experience-item">🏛️ Presidential Opening Ceremony</div>
                    <div className="experience-item">🔄 Ministerial Roundtables</div>
                    <div className="experience-item">💼 CEO & Investors Forum</div>
                    <div className="experience-item">🌍 International Investment Dialogue</div>
                    <div className="experience-item">🏦 Development Finance Forum</div>
                    <div className="experience-item">🔧 Technical Workshops</div>
                    <div className="experience-item">🤝 B2B & B2G Meetings</div>
                    <div className="experience-item">📋 Project Preparation Clinics</div>
                    <div className="experience-item">🏢 International Exhibition</div>
                    <div className="experience-item">🏪 Investment Marketplace</div>
                    <div className="experience-item">🇳🇬 Country & State Showcases</div>
                    <div className="experience-item">🎭 Gala Dinner & Awards</div>
                  </div>
                </div>
                <div className="about-section-image">
                  <img
                    src="https://images.unsplash.com/photo-1531482615713-2afd69097998?w=600"
                    alt="Summit Experience"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Who Should Attend */}
              <div className="about-section with-image reverse">
                <div className="about-section-content">
                  <h3>Who Should Attend?</h3>
                  <p>AICIS 2026 is expected to convene over 3,000 delegates, including:</p>
                  <div className="attendee-grid">
                    <div className="attendee-item">👑 Heads of State and Government</div>
                    <div className="attendee-item">🏛️ Ministers & Senior Officials</div>
                    <div className="attendee-item">🌍 Diplomatic Missions</div>
                    <div className="attendee-item">🏦 Development Finance Institutions</div>
                    <div className="attendee-item">🤝 Multilateral Organizations</div>
                    <div className="attendee-item">💰 Commercial Banks</div>
                    <div className="attendee-item">🏗️ Infrastructure Investors</div>
                    <div className="attendee-item">🌿 Climate Finance Institutions</div>
                    <div className="attendee-item">📈 Pension & Sovereign Wealth Funds</div>
                    <div className="attendee-item">💼 Private Equity Firms</div>
                    <div className="attendee-item">🏢 Corporate Executives</div>
                    <div className="attendee-item">📊 SMEs and Entrepreneurs</div>
                    <div className="attendee-item">📚 Academic & Research Institutions</div>
                    <div className="attendee-item">🤝 Development Partners</div>
                    <div className="attendee-item">🌱 Civil Society Organizations</div>
                    <div className="attendee-item">👩‍💼 Youth and Women Leaders</div>
                  </div>
                </div>
                <div className="about-section-image">
                  <img
                    src="https://images.unsplash.com/photo-1528605248644-14dd04022da1?w=600"
                    alt="Attendees"
                    loading="lazy"
                  />
                </div>
              </div>

              {/* Expected Outcomes */}
              <div className="about-section">
                <h3>Expected Outcomes</h3>
                <div className="outcomes-grid">
                  <div className="outcome-item">💰 Multi-billion-dollar investment commitments</div>
                  <div className="outcome-item">📋 Bankable infrastructure and climate project pipelines</div>
                  <div className="outcome-item">🤝 Strategic PPP agreements</div>
                  <div className="outcome-item">📄 Investment Memoranda of Understanding (MoUs)</div>
                  <div className="outcome-item">🏦 Launch of AICIF</div>
                  <div className="outcome-item">🌍 Strengthened regional and international partnerships</div>
                  <div className="outcome-item">💳 Increased access to climate finance</div>
                  <div className="outcome-item">📝 Policy recommendations for sustainable infrastructure</div>
                </div>
              </div>

              {/* Call to Action */}
              <div className="about-cta">
                <h3>Join Africa's Premier Investment Platform</h3>
                <p>
                  AICIS 2026 is more than a summit—it is a continental investment and implementation platform
                  that connects governments, investors, development partners, businesses, and innovators to
                  accelerate Africa's infrastructure transformation, climate resilience, and sustainable
                  economic growth.
                </p>
                <p className="cta-tagline">
                  <strong>Together, we move from commitments to implementation—mobilizing global finance for bankable African projects.</strong>
                </p>
                <div className="about-cta-buttons">
                  <Link to="/events/global-summit" className="about-cta-btn">
                    Register Now
                  </Link>
                  <a 
                    href="https://abvig.com/conferences" 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="about-cta-btn-secondary"
                  >
                    Learn More →
                  </a>
                </div>
              </div>
            </div>
          )}

          {/* ===== THEME TAB ===== */}
          {activeTab === 'theme' && (
            <div className="tab-content">
              <div className="about-section with-image">
                <div className="about-section-content">
                  <h3>Summit Theme</h3>
                  <div className="theme-box">
                    <h4>"From Commitments to Implementation: Mobilizing Global Finance for Bankable African Projects."</h4>
                    <p>
                      The 2026 theme reflects a strategic shift from policy dialogue to implementation,
                      focusing on mobilizing global capital, strengthening public-private partnerships,
                      and accelerating investment in commercially viable infrastructure, climate resilience,
                      and sustainable development projects across Africa.
                    </p>
                  </div>
                </div>
                <div className="about-section-image">
  <img
    src="https://images.unsplash.com/photo-1516026672322-bc52d61a55d5?w=600"
    alt="African Infrastructure Development"
    loading="lazy"
  />
</div>
              </div>

              <div className="about-section">
                <h3>Strategic Objectives</h3>
                <p>AICIS 2026 is designed to:</p>
                <ul className="objectives-list">
                  <li>Mobilize global capital for infrastructure and green investment</li>
                  <li>Accelerate project preparation and bankability</li>
                  <li>Launch the Africa Infrastructure Climate Investment Fund (AICIF)</li>
                  <li>Promote innovative financing solutions: green bonds, blended finance, carbon markets, and debt-for-climate swaps</li>
                  <li>Strengthen collaboration between governments, investors, DFIs, and the private sector</li>
                  <li>Facilitate technology transfer, innovation, and sustainable industrial development</li>
                  <li>Support investment in renewable energy, transport, agriculture, healthcare, digital infrastructure, mining, housing, and the blue economy</li>
                </ul>
              </div>
            </div>
          )}

          {/* ===== GOVERNANCE TAB - PROFESSIONAL DESIGN ===== */}
          {activeTab === 'governance' && (
            <div className="tab-content">

              {/* Governance Introduction */}
              <div className="governance-hero-section">
                <div className="governance-hero-content">
                  <span className="governance-badge">GOVERNANCE</span>
                  <h3>Summit Governance & Management</h3>
                  <div className="governance-divider"></div>
                  <p className="governance-hero-text">
                    <strong>Strategic Leadership for Africa's Premier Infrastructure & Green Investment Platform</strong>
                  </p>
                  <p>
                    The Africa Infrastructure, Climate Change, Security & Green Investment Summit (AICIS) 2026
                    is governed through a robust multi-stakeholder leadership framework that promotes
                    professionalism, transparency, accountability, collaboration, and results-driven implementation.
                  </p>
                </div>
              </div>

              {/* Governance Framework Cards */}
              <div className="governance-framework">
                <div className="framework-card">
                  <div className="framework-icon">🏛️</div>
                  <h4>Convened By</h4>
                  <p><strong>Africa Business Ventures & Investment Group (ABVIG)</strong></p>
                  <p>In collaboration with OSGF, Presidency of Nigeria, and Ministry of Foreign Affairs</p>
                </div>
                <div className="framework-card">
                  <div className="framework-icon">🤝</div>
                  <h4>Multi-Stakeholder</h4>
                  <p>Governments, DFIs, multilateral organizations, private sector, academia, and civil society</p>
                </div>
                <div className="framework-card">
                  <div className="framework-icon">🎯</div>
                  <h4>Strategic Oversight</h4>
                  <p>Policy guidance, technical expertise, and operational leadership for successful delivery</p>
                </div>
              </div>

              {/* Institutional Participation */}
              <div className="about-section">
                <h3 className="section-title-gold">Institutional Participation</h3>
                <p className="section-subtitle">The governance structure is strengthened by representatives and technical experts from:</p>
                <div className="institutional-grid-professional">
                  <div className="institutional-card">
                    <span className="institutional-number">01</span>
                    <span className="institutional-name">Office of the SGF</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">02</span>
                    <span className="institutional-name">Ministry of Foreign Affairs</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">03</span>
                    <span className="institutional-name">Federal MDAs</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">04</span>
                    <span className="institutional-name">NCCCS</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">05</span>
                    <span className="institutional-name">African Union (AU)</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">06</span>
                    <span className="institutional-name">United Nations Agencies</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">07</span>
                    <span className="institutional-name">African Development Bank</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">08</span>
                    <span className="institutional-name">Afreximbank</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">09</span>
                    <span className="institutional-name">World Bank Group</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">10</span>
                    <span className="institutional-name">Development Finance Institutions</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">11</span>
                    <span className="institutional-name">Diplomatic Missions</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">12</span>
                    <span className="institutional-name">International Development Partners</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">13</span>
                    <span className="institutional-name">Academic & Research Institutions</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">14</span>
                    <span className="institutional-name">Professional Bodies</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">15</span>
                    <span className="institutional-name">Private Sector Organizations</span>
                  </div>
                  <div className="institutional-card">
                    <span className="institutional-number">16</span>
                    <span className="institutional-name">Civil Society Organizations</span>
                  </div>
                </div>
              </div>

              {/* Governance Principles */}
              <div className="about-section">
                <h3 className="section-title-gold">Governance Principles</h3>
                <p className="section-subtitle">AICIS 2026 is guided by internationally recognized governance principles:</p>
                <div className="principles-grid-professional">
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">🎯</span>
                    <span>Strategic Leadership</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">🔍</span>
                    <span>Transparency & Accountability</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">🤝</span>
                    <span>Public-Private Partnership</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">🤝</span>
                    <span>Collaboration</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">⭐</span>
                    <span>Professional Excellence</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">💡</span>
                    <span>Innovation</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">🌿</span>
                    <span>Sustainability</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">🌍</span>
                    <span>Climate Resilience</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">📊</span>
                    <span>Investment Readiness</span>
                  </div>
                  <div className="principle-card-professional">
                    <span className="principle-icon-pro">📈</span>
                    <span>Results-Based Management</span>
                  </div>
                </div>
              </div>

              {/* Summit Secretariat */}
              <div className="about-section">
                <h3 className="section-title-gold">Summit Secretariat</h3>
                <p className="section-subtitle">The AICIS Secretariat coordinates the day-to-day planning and implementation:</p>
                <div className="secretariat-grid-professional">
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">📋</span>
                    <span>Programme Management</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">🏛️</span>
                    <span>Government Relations</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">🌍</span>
                    <span>Diplomatic Coordination</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">🤝</span>
                    <span>Sponsorship & Partnerships</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">👥</span>
                    <span>Delegate Services</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">📋</span>
                    <span>Protocol & Logistics</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">📢</span>
                    <span>Media & Communications</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">📝</span>
                    <span>Registration & Accreditation</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">💰</span>
                    <span>Finance & Administration</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">🏢</span>
                    <span>Exhibition Management</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">💼</span>
                    <span>Investment Facilitation</span>
                  </div>
                  <div className="secretariat-card-pro">
                    <span className="secretariat-icon-pro">📊</span>
                    <span>Monitoring & Evaluation</span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* ===== COMMITTEE TAB ===== */}
          {activeTab === 'committee' && (
            <div className="tab-content">
              {/* Planning & Organising Committee */}
              <div className="about-section">
                <h3>Planning & Organising Committee</h3>
                <p>
                  The Planning & Organising Committee is responsible for the overall planning, coordination,
                  stakeholder engagement, partnership development, fundraising, and implementation of AICIS 2026.
                </p>
              </div>

              {/* Chairman */}
              <div className="committee-member-card featured">
                <div className="committee-member-photo">
                  <img
                    src="/images/speakers/moses-owharo.jpeg"
                    alt="Hon. Moses O. T. Owharo"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.parentElement.innerHTML = '<div class="committee-placeholder-large">👤</div>';
                    }}
                  />
                </div>
                <div className="committee-member-info">
                  <span className="committee-role-badge">CHAIRMAN</span>
                  <h4>Hon. Moses O. T. Owharo</h4>
                  <p className="committee-title">Chairman, Planning & Organising Committee</p>
                  <p className="committee-bio">
                    Hon. Moses O. T. Owharo is an international development strategist, investment promotion
                    specialist, and infrastructure advocate with extensive experience in public-private
                    partnerships (PPPs), project development, capital mobilisation, and international
                    business diplomacy.
                  </p>
                  <p className="committee-bio">
                    He is the <strong>Chairman/Chief Executive Officer of Africa Business Ventures & Investment Group (ABVIG)</strong>,
                    Convenor of AICIS, Founder of the International Community of Africans in Diaspora (ICAD),
                    and President & Founder of Aidez Small Project International (ASPI).
                  </p>
                  <p className="committee-bio highlight">
                    As Chairman, he provides strategic leadership for government relations, diplomatic engagement,
                    partnership development, investment mobilisation, and the overall implementation of AICIS 2026.
                  </p>
                </div>
              </div>

              {/* Technical Advisory Board */}
              <h4 style={{ marginTop: '40px', marginBottom: '20px', color: 'var(--logo-brown, #2B1007)' }}>
                Technical Advisory Board
              </h4>
              <p style={{ marginBottom: '25px' }}>
                The Technical Advisory Board comprises distinguished leaders from diplomacy, academia, finance,
                infrastructure, security, climate policy, and international development.
              </p>

              <div className="committee-grid-about">
                {/* Prof. Ladan */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/speakers/prof-ladan.jpg"
                      alt="Prof. M. T. Ladan"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">📚</div>';
                      }}
                    />
                  </div>
                  <h4>Prof. M. T. Ladan</h4>
                  <p className="committee-role-label">Technical Advisor – Regional Integration</p>
                  <p className="committee-bio-short">
                    Leading scholar in regional economic integration, sustainable development, and international law.
                  </p>
                </div>

                {/* H.E. Amb. Faruk Malami Yabo, MFR */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/speakers/Faruk-Yabo.jpg"
                      alt="H.E. Amb. Faruk Malami Yabo, MFR"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">🌍</div>';
                      }}
                    />
                  </div>
                  <h4>H.E. Amb. Faruk Malami Yabo, MFR</h4>
                  <p className="committee-role-label">Technical Advisor – Diplomacy</p>
                  <p className="committee-bio-short">
                    Distinguished diplomat and former Ambassador to Jordan with concurrent accreditation to Iraq.
                  </p>
                </div>

                {/* H.E. Amb. (Mrs.) Mobalaji S. Ogundero */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/speakers/Mobalaji.jpeg"
                      alt="H.E. Amb. (Mrs.) Mobalaji S. Ogundero"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">🌍</div>';
                      }}
                    />
                  </div>
                  <h4>H.E. Amb. (Mrs.) Mobalaji S. Ogundero</h4>
                  <p className="committee-role-label">Technical Advisor – International Cooperation</p>
                  <p className="committee-bio-short">
                    Career diplomat and former Head of Mission at the Embassy of Nigeria in Washington, D.C.
                  </p>
                </div>

                {/* Prof. Chris Schrage */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/speakers/Prof-Chris.jpg"
                      alt="Prof. Chris Schrage"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">📚</div>';
                      }}
                    />
                  </div>
                  <h4>Prof. Chris Schrage</h4>
                  <p className="committee-role-label">Technical Advisor – Economic Development</p>
                  <p className="committee-bio-short">
                    Professor at University of Northern Iowa, USA, and Certified Global Business Professional.
                  </p>
                </div>

                {/* Chief Felix Agbaudu, AIG (Rtd.) */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/speakers/Felix-ogbaudu.jpg"
                      alt="Chief Felix Agbaudu, AIG (Rtd.)"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">🛡️</div>';
                      }}
                    />
                  </div>
                  <h4>Chief Felix Agbaudu, AIG (Rtd.)</h4>
                  <p className="committee-role-label">Technical Advisor – Security & Risk</p>
                  <p className="committee-bio-short">
                    Retired Assistant Inspector General of Police with decades of national security leadership.
                  </p>
                </div>

                {/* Prof. S. R. Ogunduyile */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/Prof-sunday.jpeg"
                      alt="Prof. S. R. Ogunduyile"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">🌍</div>';
                      }}
                    />
                  </div>
                  <h4>Prof. S. R. Ogunduyile</h4>
                  <p className="committee-role-label">Planning & Organising Committee Member – Advisor, Industrialization & Project Development</p>
                  <p className="committee-bio-short">
                    Professor in the Department of Industrial Design, School of Environmental Technology, Federal University of Technology, Akure (FUTA), Nigeria.
                  </p>
                </div>

                {/* Mr. Philip Okuabor */}
                <div className="committee-member-card">
                  <div className="committee-member-photo-small">
                    <img
                      src="/images/speakers/Philip-Okuabor.jpg"
                      alt="Mr. Philip Okuabor"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.parentElement.innerHTML = '<div class="committee-placeholder-small">💰</div>';
                      }}
                    />
                  </div>
                  <h4>Mr. Philip Okuabor</h4>
                  <p className="committee-role-label">Technical Advisor – Banking & Finance</p>
                  <p className="committee-bio-short">
                    Retired senior banking executive with Zenith Bank Plc, expert in project finance.
                  </p>
                </div>
              </div>

              <div className="about-section" style={{ marginTop: '40px' }}>
                <p style={{ fontStyle: 'italic', color: '#666', textAlign: 'center' }}>
                  As committee membership continues to expand, additional profiles and institutional
                  representatives will be published on this page.
                </p>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ===== BACK BUTTON ===== */}
      <div className="about-aicis-back">
        <Link to="/events" className="back-button">← Back to Events</Link>
      </div>
    </div>
  );
};

export default AboutAICIS;